import React from "react";
import { PrismAsync as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneLight } from "react-syntax-highlighter/dist/esm/styles/prism";
import { parseMarkdownText, isOrdered } from "utils/markdown";
// Some styles overridden in ChatGPT scss file to use pseudoselectors for AgentIQ
export const DEFAULT_OVERRIDES = {
    p: {
        style: {
            fontSize: "14px",
            lineHeight: "25px",
            minHeight: "25px"
        }
    },
    h5: {
        style: {
            fontSize: "15px",
            lineHeight: "1.3"
        }
    },
    h4: {
        style: {
            fontSize: "16px",
            lineHeight: "1.3"
        }
    },
    h3: {
        style: {
            fontSize: "20px",
            lineHeight: "1.4"
        }
    },
    table: {
        style: {
            width: "100%",
            borderCollapse: "collapse",
            margin: "1rem 0",
            fontFamily: "sans-serif"
        }
    },
    th: {
        style: {
            border: "1px solid #ddd",
            padding: "8px 12px",
            textAlign: "left"
        }
    },
    td: {
        style: {
            border: "1px solid #ddd",
            padding: "8px 12px",
            textAlign: "left"
        }
    },
    thead: {
        style: {
            backgroundColor: "#f4f4f4",
            fontWeight: "bold"
        }
    },
    hr: {
        style: {
            marginTop: "1rem",
            marginBottom: "1rem",
            border: "0",
            borderTop: "1px solid rgba(0, 0, 0, .1)"
        }
    },
    ul: {
        style: {
            listStyle: "circle",
            marginLeft: "20px",
            paddingBottom: "10px"
        }
    },
    ol: {
        style: {
            listStyle: "auto",
            marginLeft: "20px",
            paddingBottom: "10px"
        }
    },
    blockquote: {
        style: {
            margin: "1.5em 10px",
            padding: "0.5em 10px",
            borderLeft: "5px solid #ccc",
            backgroundColor: "#f9f9f9",
            color: "#555",
            fontSize: "inherit"
        }
    },
    code: {
        style: {
            whiteSpace: "normal",
            background: "#FAFAFA",
            border: "1px solid #E1E1E1",
            borderRadius: "4px",
            color: "#202939"
        }
    }
};
const defaultRenderLeaf = (leaf) => {
    if (typeof leaf === "string") {
        return React.createElement(React.Fragment, null, leaf);
    }
    return React.createElement(React.Fragment, null);
};
const getDefaultTagRenderer = () => ({});
function MarkdownElementCollection({ children, overrides, renderLeaf, xmlTagRenderFunctions }) {
    return (React.createElement(React.Fragment, null, children.map((node, i) => (React.createElement(MarkdownElement, { key: i, overrides: overrides, renderLeaf: renderLeaf, xmlTagRenderFunctions: xmlTagRenderFunctions }, node)))));
}
function MarkdownElement({ children, overrides, renderLeaf, xmlTagRenderFunctions }) {
    if (typeof children === "string") {
        return renderLeaf(children);
    }
    else if (children.type === "data") {
        return renderLeaf(children.data);
    }
    let content;
    if (children.children.length == 0) {
        content = React.createElement(React.Fragment, null);
    }
    else if (children.children.length == 1) {
        content = (React.createElement(MarkdownElement, { overrides: overrides, renderLeaf: renderLeaf, xmlTagRenderFunctions: xmlTagRenderFunctions }, children.children[0]));
    }
    else {
        content = (React.createElement(MarkdownElementCollection, { overrides: overrides, renderLeaf: renderLeaf, xmlTagRenderFunctions: xmlTagRenderFunctions }, children.children));
    }
    if (children.type == "li" && isOrdered(children)) {
        if (overrides[children.type] !== undefined) {
            return (React.createElement("li", { ...overrides[children.type], value: children.value }, content));
        }
        else {
            return React.createElement("li", { value: children.value }, content);
        }
    }
    if (children.type == "code_block") {
        if (children.language === "markdown") {
            return (React.createElement(MarkdownElementCollection, { overrides: overrides, renderLeaf: renderLeaf, xmlTagRenderFunctions: xmlTagRenderFunctions }, parseMarkdownText(children.children.join("\n"))));
        }
        const codeBlockOverrides = overrides.code_block;
        if (codeBlockOverrides?.wrap === "soft") {
            return (React.createElement(SyntaxHighlighter, { language: children.language, style: oneLight }, children.children.join("\n").trim()));
        }
        return (React.createElement(SyntaxHighlighter, { language: children.language, style: oneLight, wrapLongLines: true, 
            //Lines aren't actually wrapping, so we're using this hack
            lineProps: { style: { wordBreak: "normal", whiteSpace: "pre-wrap" } }, wrapLines: true }, children.children.join("\n").trim()));
    }
    else if (children.type == "fragment") {
        return React.createElement(React.Fragment, null, content);
    }
    else if (children.type == "xml") {
        if (xmlTagRenderFunctions[children.tag] !== undefined) {
            const ChildElement = xmlTagRenderFunctions[children.tag];
            return React.createElement(ChildElement, null, content);
        }
        let xmlChildren = [];
        if (children.children.length > 0) {
            xmlChildren = [{ type: "span", children: [`<${children.tag}>`] }];
            xmlChildren = xmlChildren.concat(children.children);
        }
        if (children.closed) {
            xmlChildren.push({ type: "span", children: [`<\\${children.tag}>`] });
        }
        return (React.createElement(MarkdownElement, { overrides: overrides, renderLeaf: renderLeaf, xmlTagRenderFunctions: xmlTagRenderFunctions }, {
            type: "fragment",
            children: xmlChildren
        }));
    }
    if (children.type == "hr") {
        return React.createElement("hr", { ...overrides[children.type] });
    }
    return React.createElement(children.type, overrides[children.type], content);
}
export default function MarkdownPanel({ children, overrides, renderLeaf, xmlTagRenderFunctions }) {
    const props = {
        overrides: { ...DEFAULT_OVERRIDES, ...overrides },
        renderLeaf: renderLeaf ?? defaultRenderLeaf,
        xmlTagRenderFunctions: xmlTagRenderFunctions ?? getDefaultTagRenderer()
    };
    if (typeof children === "string") {
        return React.createElement(MarkdownElementCollection, { ...props }, parseMarkdownText(children));
    }
    else if (children === undefined) {
        return React.createElement(React.Fragment, null);
    }
    else if (Array.isArray(children)) {
        return React.createElement(MarkdownElementCollection, { ...props }, children);
    }
    return React.createElement(MarkdownElement, { ...props }, children);
}
